// frontend/src/services/outreachService.js
// API client for admin outreach endpoints (suppression list management).
//
// NOTE: The shared `api` axios instance has baseURL normalized to end with
// `/api`. Paths passed to api.get/post MUST NOT include the `/api/` prefix.
// The `exportCsvUrl()` helper is the exception — it is intended for use as
// an `<a href>` for CSV downloads, so it returns the full path including
// `/api/`.
import { api } from './api';

export const outreachService = {
  // List suppression entries. Optional filter by reason, with pagination.
  listSuppression: async ({ reason, limit = 100, offset = 0 } = {}) => {
    const params = new URLSearchParams({ limit, offset });
    if (reason) params.set('reason', reason);
    const response = await api.get(`/admin/outreach/suppression?${params}`);
    return response.data;
  },

  // Add an email or domain to the suppression list.
  addSuppression: async ({ email, domain, reason, notes }) => {
    const response = await api.post('/admin/outreach/suppression', {
      email: email || undefined,
      domain: domain || undefined,
      reason,
      notes,
    });
    return response.data;
  },

  // List recent agent runs. Optional filter by agent_type ('sales' | 'marketing').
  listRuns: async ({ agentType, limit = 50 } = {}) => {
    const params = new URLSearchParams({ limit });
    if (agentType) params.set('agent_type', agentType);
    const response = await api.get(`/admin/outreach/runs?${params}`);
    return response.data;
  },

  // URL used directly as an `<a href>` to trigger a CSV download.
  // Bypasses the axios request pipeline, so we compose the full URL from the
  // shared instance's baseURL (already normalized to end with `/api`). This
  // works whether the SPA is same-origin with the API or served from a
  // separate origin (e.g. dev against localhost:8000, split CDN deploys).
  exportCsvUrl: () => {
    const base = (api.defaults.baseURL || '/api').replace(/\/$/, '');
    return `${base}/admin/outreach/suppression/export.csv`;
  },

  // Per-inbox warmup maturity snapshot. Drives the queue banner and is
  // mirrored server-side at approve-time so the Approve button + the actual
  // dispatch guard can never disagree.
  getInboxHealth: async () => {
    const response = await api.get('/admin/outreach/inbox-health');
    return response.data;
  },

  // List drafts pending operator review.
  listPendingDrafts: async ({ tier, limit = 50 } = {}) => {
    const params = new URLSearchParams({ limit, status: 'pending_review' });
    if (tier) params.set('tier', tier);
    const response = await api.get(`/admin/outreach/drafts?${params}`);
    return response.data;
  },

  // Approve a draft → triggers send via Instantly.
  approveDraft: async (draftId) => {
    const response = await api.post(`/admin/outreach/drafts/${draftId}/approve`);
    return response.data;
  },

  // Reject a draft → marks rejected with optional reason.
  rejectDraft: async (draftId, { reason } = {}) => {
    const response = await api.post(`/admin/outreach/drafts/${draftId}/reject`, { reason });
    return response.data;
  },

  // Edit subject / body before approving.
  editDraft: async (draftId, { subject, body } = {}) => {
    const response = await api.patch(`/admin/outreach/drafts/${draftId}`, {
      subject, body,
    });
    return response.data;
  },
};

export default outreachService;
